import React, { FC, useState } from 'react'

import TabTile from 'tabs/data/components/TabTile'
import { Highlighted } from 'tabs/data/components/Highlighted'
import ColumnsForm from './ColumnsForm'
import { NewDatasetColumns } from './NewDatasetColumns'
import { useDatasetColumns } from 'tabs/data/pages/descriptions/hooks/useDatasetColumns'
import { alertError, alertSuccess } from 'api'
import { DaxUsagesResponse, Table } from 'tabs/data/api/generated'
import { deleteTableColumn, UpdateDatasetColumnsParams } from 'tabs/data/api/columns'
import { RelatedResourcesModal } from 'tabs/data/components/RelatedResourcesModal'
import { cloneDeep } from 'lodash'

type DatasetTablesProps = {
  highlight: string
  datasetId: string
  tableId: string
  columns: Table['columns']
  refreshCallback: () => Promise<void>
}

export const DatasetColumns: FC<DatasetTablesProps> = ({ datasetId, tableId, highlight, columns, refreshCallback }) => {
  const { updateColumn } = useDatasetColumns()

  const [warningData, setWarningData] = useState<null | DaxUsagesResponse>(null)
  const [forceUpdateParams, setForceUpdateParams] = useState<{
    columnId?: string
    params?: UpdateDatasetColumnsParams
  } | null>(null)
  const [expandedColumn, setExpandeMetrics] = useState('')

  const handleChangeExpandedMetrics = (metricId: string) => {
    const nextValue = expandedColumn !== metricId ? metricId : ''
    setExpandeMetrics(nextValue)
  }

  const handleUpdateColumn = async (columnId: string, params: UpdateDatasetColumnsParams, forceUpdate = false) => {
    try {
      const responce = await updateColumn(columnId, datasetId, tableId, forceUpdate, params)

      if (responce) {
        setWarningData(null)
        setForceUpdateParams(null)
        alertSuccess('Column updated', 3000)
        await refreshCallback()
      }
    } catch (error) {
      switch (error.response.status) {
        case 404:
          throw alertError('The table that was already deleted')
        case 409:
          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.usages) {
              setForceUpdateParams({ columnId, params: cloneDeep(params) })
              return setWarningData(errorData)
            }
          }

          throw alertError("Conflict. Can't use the name")
        case 412:
          setWarningData(null)
          throw alertError('Error! Someone already made changes. Please reload page and try again.')
        default:
          setWarningData(null)

          if (error?.response) {
            const errorData = await error.response.json()

            if (errorData?.errorMessage) throw alertError(errorData?.errorMessage)
          }

          throw alertError(error?.message || error || 'Error updating column')
      }
    }
  }

  const handelForceUpdateColumn = async () => {
    await handleUpdateColumn(forceUpdateParams.columnId, forceUpdateParams.params, true)
    setForceUpdateParams(null)
  }

  const handleColumnDelete = async (columnId: string, force: boolean) => {
    try {
      await deleteTableColumn({ datasetId, tableId, columnId, force })
      alertSuccess('Column deleted', 3000)
      await refreshCallback()
    } catch (error) {
      if (error.response.status === 404) {
        throw alertError('The table that was already deleted')
      }

      throw error
    }
  }

  return (
    <div
      style={{
        background: 'var(--color-selago-grey)',
        margin: '0 -1rem',
        padding: '0 1rem 1rem',
        borderRadius: '10px',
      }}>
      {columns?.map((columnItem, idx) => (
        <TabTile
          key={columnItem?.columnId + idx}
          tab={{
            name: (
              <Highlighted
                text={`${columnItem.columnName}${!columnItem.isActive ? ' (Inactive)' : ''}`}
                highlight={highlight}
              />
            ),
            icon: `${idx + 1}`,
          }}
          expanded={expandedColumn === columnItem.columnId}
          onChange={() => handleChangeExpandedMetrics(columnItem.columnId)}>
          {expandedColumn === columnItem.columnId && (
            <ColumnsForm
              updatedAt={columnItem.updatedAt}
              inputText={columnItem.columnName}
              type={columnItem.columnType}
              rules={columnItem.rules}
              synonyms={columnItem.columnSynonyms}
              isActive={columnItem.isActive ?? true}
              textAreaContent={columnItem.columnDescription}
              cancelButtonLabel="Reset"
              onSubmit={async params => {
                handleUpdateColumn(columnItem.columnId, { ...params, version: columnItem.columnVersion })
              }}
              updateCallback={refreshCallback}
              onDelete={handleColumnDelete.bind(null, columnItem.columnId)}
            />
          )}
        </TabTile>
      ))}

      <NewDatasetColumns
        datasetId={datasetId}
        tableId={tableId}
        hasItems={columns?.length > 0}
        onSubmit={refreshCallback}
      />

      <RelatedResourcesModal
        open={Boolean(warningData?.usages)}
        data={warningData?.usages || {}}
        submitLabel="Save Anyway"
        message={`"${warningData?.name || 'Name'}" is used in the following places:`}
        onClose={() => setWarningData(null)}
        onSubmit={handelForceUpdateColumn}
      />
    </div>
  )
}
